<!--
  FrameX right panel: Frames | Pages tabs. Wraps the same <Sidebar> chrome the
  legacy shpx panel used; props mirror what Document.svelte's right-panel block
  passed, and child events forward up unchanged. See wiki: framex (Right panel).
-->
<script>
  import Sidebar from "#lib/sidebar/Sidebar.svelte";
  import SidebarSection from "#lib/sidebar/SidebarSection.svelte";
  import ItemProperties from "#lib/sidebar/ItemProperties.svelte";
  import PageProperties from "#lib/sidebar/PageProperties.svelte";
  import LocationProperties from "#lib/sidebar/LocationProperties.svelte";
  import FrameProperties from "./FrameProperties.svelte";
  import PagesTab from "./PagesTab.svelte";
  import FrameMarkersList from "./FrameMarkersList.svelte";
  import ScopeTotals from "./ScopeTotals.svelte";
  import SelectionBar from "./SelectionBar.svelte";
  import { workingSheetIds, sheetFrames, framesNotInFrameX } from "../derive.js";
  import { setPageFlags } from "../page-flags.js";
  import {
    itemOverlay,
    expandRightPanel,
    rightPanelTab,
    selectedPages,
    selectedLocations,
  } from "#src/stores/ui.js";

  // Sidebar chrome
  export let sidebarPosition;

  // Tab bar
  export let pageCount;

  // Working-set page ids in document order (defaulted so the call site
  // type-checks even before it passes the prop).
  export let visiblePages = [];

  // Shared document context
  export let group;
  export let doc;
  export let origDoc;
  export let isVirtual;
  export let disabled;
  export let items;
  export let types;
  export let collections;
  export let documentNotes;
  export let customColumns;
  export let customColColumns;
  export let standardColumns;

  // Properties-tab contextual dispatch data (mirrors the block this replaces)
  export let indexedLocations;
  export let locationsByRecord;
  export let visibleIndices;
  export let collectionItems;
  export let selectedPageList;
  export let selectedLocationList;
  export let selectedDimensionList;
  export let locations; // filtered locations for the current doc (LocationProperties' `docLocations` prop)
  export let adding;
  export let spinners;
  export let hoveredLocations;

  // Pages-tab data
  export let docLocations; // full { docId -> locations[] } map (PageList's `docLocations` prop)
  export let filters;
  export let hoveredPage;
  export let lastSelectedPage;

  // FrameMarkersList owns the sort/select mutation logic for these two —
  // bound here only so the ScopeTotals region below can key off `groupBy`.
  let groupBy = "none";
  let sortMode = "mark-asc";

  // `doc.data.pages` is the same map FrameMarkersList's `pages` prop expects
  // (sheet number half of its page label) — derived here rather than added
  // as a new prop since `doc` is already passed in.
  $: pages = doc?.data?.pages;
  $: workingIds = workingSheetIds(pages, visiblePages);
  $: frames = workingIds.flatMap((id) => sheetFrames(indexedLocations[id]));

  // "Not in FrameX" — frame placements on sheets not in the working set. A
  // removed sheet keeps its scale and stays on the canvas, so these are
  // ordinary placements, rendered by OrphanFramesSection below.
  $: otherFrames = framesNotInFrameX(indexedLocations, visiblePages, workingIds);

  // Recovery: put a removed sheet back into FrameX by clearing its membership
  // flag (consistent with FrameXPanel's add path) — scale and N/A are untouched.
  function addSheetBack(e) {
    const pageId = e.detail?.page;
    if (pageId) setPageFlags(group, doc, [pageId], { framexExcluded: false });
  }

  // Follow the selection to the matching tab: selecting frame(s) — on canvas or
  // in the list — shows the Frames tab, selecting page(s) shows the Pages tab.
  // Keyed on the selection identity so it only fires when the selection
  // actually changes; the user can still switch tabs manually while a
  // selection stands.
  let prevFrameSel = "";
  let prevPageSel = "";
  $: frameSel = selectedLocationList.map((l) => l.id).join(",");
  $: pageSel = selectedPageList.map((p) => p.id).join(",");
  // Frame and page selections are mutually exclusive: picking one clears the
  // other. The length guard avoids writing (and repainting the canvas off) an
  // already-empty store, and stops the watchers looping — clearing a store
  // drives its own `Sel` to "" so its watcher's `if` is then false.
  $: if (frameSel !== prevFrameSel) {
    if (frameSel) {
      $rightPanelTab = "properties";
      if (Object.keys($selectedPages).length) selectedPages.set({});
    }
    prevFrameSel = frameSel;
  }
  $: if (pageSel !== prevPageSel) {
    if (pageSel) {
      $rightPanelTab = "pages";
      if (Object.keys($selectedLocations).length) selectedLocations.set({});
    }
    prevPageSel = pageSel;
  }
</script>

<Sidebar
  position={sidebarPosition}
  type={sidebarPosition === "bottom" ? "absolute" : "flex"}
  expandable
  expandDisabled={!!$itemOverlay}
  bind:expanded={$expandRightPanel}>
  <svelte:fragment slot="header">
    <div class="fx-rp-tabs">
      <button
        type="button"
        class="fx-rp-tab"
        class:active={$rightPanelTab === "properties"}
        on:click={() => ($rightPanelTab = "properties")}>
        Frames ({frames.length})
      </button>
      <button
        type="button"
        class="fx-rp-tab"
        class:active={$rightPanelTab === "pages"}
        class:fx-rp-tab--disabled={$itemOverlay}
        disabled={!!$itemOverlay}
        on:click={() => {
          if ($itemOverlay) return;
          $rightPanelTab = "pages";
        }}>
        Pages ({pageCount})
      </button>
    </div>
    {#if $rightPanelTab === "properties" && $itemOverlay}
      {@const item = $group.items[$itemOverlay]}
      <div class="sticky top-0 bg-white z-10 py-4">
        <SidebarSection>
          <div class="text-center font-bold truncate">{item?.mark}</div>
        </SidebarSection>
      </div>
    {/if}
    <!-- No per-frame Prev/Next "Location: <mark>" header: it shifted the panel
         on every frame selection, which read as disorienting in FrameX. -->
  </svelte:fragment>
  <svelte:fragment slot="content">
    {#if $rightPanelTab === "pages"}
      <div class="fx-pages-column">
        <div class="fx-pages-scroll">
          <PagesTab
            {group}
            {doc}
            {isVirtual}
            {docLocations}
            {disabled}
            {filters}
            bind:hoveredPage
            bind:lastSelectedPage
            on:zoom-to-page
            on:update-visible-pages />
        </div>
        {#if selectedPageList.length > 0}
          <div class="fx-docked-section">
            <PageProperties
              framex
              {disabled}
              {group}
              {origDoc}
              {doc}
              {indexedLocations}
              {isVirtual}
              pages={selectedPageList}
              on:zoom-to-page />
          </div>
        {/if}
      </div>
    {:else}
      <!-- Frames tab: scrolling markers list, ScopeTotals (Scope grouping
           only), docked properties, then the selection bar. -->
      <div class="fx-properties-column">
        <FrameMarkersList
          {group}
          {frames}
          {pages}
          {visibleIndices}
          orphanFrames={otherFrames}
          bind:hoveredLocations
          bind:sortMode
          bind:groupBy
          on:zoom-to-page
          on:add-sheet={addSheetBack} />

        {#if groupBy === "scope"}
          <ScopeTotals {group} {frames} />
        {/if}

        <div class="fx-properties-docked">
          {#if $itemOverlay}
            {@const item = $group.items[$itemOverlay]}
            {#if item}
              <ItemProperties
                {disabled}
                {group}
                {item}
                itemid={$itemOverlay}
                {types}
                {collections}
                customColumns={item.is_collection ? customColumns : customColColumns}
                {standardColumns}
                isCollection={item.is_collection}
                settings={$group.data.settings}
                showDrawingScale
                on:updateItem />
            {/if}
          {:else if selectedLocationList.length > 0}
            <!--
              FrameX: selected frame(s) show a reduced property set — Frame
              Scale, Mark, Width, Height, Flag — via FrameProperties, for both
              single and multi selection (Mixed values across a multi-select).
              The legacy DocLocationProperties frame list is intentionally not
              rendered here: the FrameMarkersList above already is the frame
              list, and bulk edit/delete live in the selection bar.
            -->
            <FrameProperties
              {group}
              {doc}
              {disabled}
              locations={selectedLocationList}
              {types}
              {collections}
              {customColumns}
              {standardColumns} />
          {:else if selectedDimensionList.length > 0}
            <LocationProperties
              locations={selectedLocationList}
              dimensions={selectedDimensionList}
              docLocations={locations}
              {doc}
              {group}
              {types}
              {items}
              {locationsByRecord}
              {collections}
              {documentNotes}
              {disabled}
              {customColumns}
              {customColColumns}
              {collectionItems}
              {standardColumns}
              {visibleIndices}
              bind:spinners
              on:add-location
              on:clone-location
              on:updateSupplier />
          {/if}
        </div>

        <SelectionBar {group} {selectedLocationList} on:delete-frames />
      </div>
    {/if}
  </svelte:fragment>
</Sidebar>

<style lang="scss">
  // Properties tab, three stacked regions. Sidebar's own "content" slot
  // wrapper (h-full flex-col ancestor, see Sidebar.svelte) gives this a
  // definite height to size against.
  .fx-properties-column {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    // Establish the legacy text-xs base (matching <SidebarSection>) so every
    // control inside — SelectInput, TextInput, rows — inherits 12px with the
    // tight 1rem line-height instead of the browser default.
    font-size: 12px;
    line-height: 1rem;
  }

  // The existing contextual dispatch (ItemProperties/PageProperties/
  // LocationProperties/DocLocationProperties), docked and height-capped —
  // its own internal markup/scroll behavior is unchanged.
  // The Properties section holds a fixed, short set of fields (Frame Scale,
  // Mark, Width, Height, Flag), so it sits at its natural height with no scroll
  // — the frames list above takes the remaining space.
  .fx-properties-docked {
    flex: none;
  }

  // Page section separator — same treatment as the frame Properties section
  // (FrameProperties' own .fx-frame-props): a full-width rule flush to the
  // frames list, then space before the "Page" heading.
  .fx-docked-section {
    padding-top: 10px;
    border-top: 1px solid var(--fx-border-light, #eceef1);
  }

  // Pages tab, same shape as the properties column: the page list takes the
  // available space and scrolls, the selected page's Properties pin below it.
  .fx-pages-column {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  .fx-pages-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }

  .fx-rp-tabs {
    display: flex;
    border-bottom: 1px solid var(--fx-border-light, #eceef1);
    padding: 0 4px;
    user-select: none;
  }

  .fx-rp-tab {
    all: unset;
    cursor: pointer;
    flex: none;
    padding: 10px 12px 8px;
    font-size: 12px;
    font-weight: 700;
    color: var(--fx-neutral, #9ca3af);
    border-bottom: 2px solid transparent;

    &.active {
      color: var(--fx-text, #1a1d21);
      border-bottom-color: var(--fx-accent, #3b7dd8);
    }

    &:hover:not(.active) {
      color: var(--fx-text-alt, #374151);
    }

    // Edit mode: the Pages tab stays visible but dimmed and inert. Match the
    // left panel — a normal cursor (override the base pointer) and no hover
    // affordance, not a "not-allowed" badge.
    &--disabled {
      opacity: 0.4;
      cursor: default;

      &:hover:not(.active) {
        color: var(--fx-neutral, #9ca3af);
      }
    }
  }
</style>
